class ScrollAnimation extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.totalFrames = parseInt(this.dataset.frames, 10);
    this.baseUrl = this.dataset.baseUrl;
    this.scrollSection = document.getElementById(this.dataset.section);

    this.images = new Array(this.totalFrames + 1);
    this.currentFrame = 0;
    this.rafPending = false;
    this._contentOffset = null;
    this._loadingSet = new Set();
    this._loadBatchSize = 8;
    this._loadQueue = [];

    this._onScroll = this.onScroll.bind(this);
    this._onResize = this.onResize.bind(this);

    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });

    this.setSize();
    this.loadFrames();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
  }

  getUrl(n) {
    return this.baseUrl.replace('0000', String(n).padStart(4, '0'));
  }

  setSize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  isReady(img) {
    if (!img) return false;
    if (img instanceof ImageBitmap) return true;
    return img.complete && img.naturalWidth > 0;
  }

  draw(img) {
    if (!this.isReady(img)) return;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;
    const scale = Math.min(w / imgW, h / imgH);
    const x = (w - imgW * scale) / 2;
    const y = (h - imgH * scale) / 2;
    this.ctx.clearRect(0, 0, w, h);
    this.ctx.drawImage(img, x, y, imgW * scale, imgH * scale);
  }

  onScroll() {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      if (!this.scrollSection) return;
      const rect = this.scrollSection.getBoundingClientRect();
      const startAt = window.innerHeight - this.getContentOffset();
      const endAt = -(this.scrollSection.offsetHeight - window.innerHeight);
      const progress = Math.max(0, Math.min(1, (startAt - rect.top) / (startAt - endAt)));
      const frame = Math.min(Math.round(progress * this.totalFrames), this.totalFrames);
      this.loadNearby(frame);
      if (frame !== this.currentFrame || !this.isReady(this.images[frame])) {
        this.currentFrame = frame;
        this.draw(this.images[frame]);
      }
    });
  }

  getContentOffset() {
    if (this._contentOffset === null) {
      const el = this.querySelector('[data-animation-content]');
      this._contentOffset = el ? el.offsetTop + el.offsetHeight : 0;
    }
    return this._contentOffset;
  }

  onResize() {
    this._contentOffset = null;
    this.setSize();
    this.draw(this.images[this.currentFrame]);
  }

  loadImage(i) {
    if (this.images[i] || this._loadingSet.has(i) || i < 0 || i > this.totalFrames) return false;
    this._loadingSet.add(i);
    const img = new Image();
    img.decoding = 'async';
    const done = () => {
      this._loadingSet.delete(i);
      this.drainQueue();
    };
    img.onload = () => {
      if (typeof createImageBitmap === 'function') {
        createImageBitmap(img).then((bmp) => {
          this.images[i] = bmp;
          if (i === this.currentFrame) this.draw(bmp);
          done();
        });
      } else {
        this.images[i] = img;
        if (i === this.currentFrame) this.draw(img);
        done();
      }
    };
    img.onerror = done;
    img.src = this.getUrl(i);
    return true;
  }

  drainQueue() {
    while (this._loadQueue.length && this._loadingSet.size < this._loadBatchSize) {
      const idx = this._loadQueue.shift();
      if (!this.images[idx] && !this._loadingSet.has(idx)) {
        this.loadImage(idx);
      }
    }
  }

  loadNearby(center) {
    const queue = [];
    const ahead = 30;
    const behind = 5;
    for (let d = 0; d <= ahead; d++) {
      const f = center + d;
      if (f >= 0 && f <= this.totalFrames && !this.images[f] && !this._loadingSet.has(f)) queue.push(f);
      if (d > 0 && d <= behind) {
        const b = center - d;
        if (b >= 0 && b <= this.totalFrames && !this.images[b] && !this._loadingSet.has(b)) queue.push(b);
      }
    }
    this._loadQueue = queue;
    this.drainQueue();
  }

  loadFrames() {
    const first = new Image();
    first.onload = () => {
      this.images[0] = first;
      this.setSize();
      this.draw(first);
      this.loadNearby(0);
    };
    first.src = this.getUrl(0);
  }
}

if (!customElements.get('scroll-animation')) {
  customElements.define('scroll-animation', ScrollAnimation);
}
